import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { PageHero } from '@/components/layout/PageHero';
import { documentNumberField } from '../lib/documentNumber';
import { DocumentIdentityStrip, type IdentityField } from './DocumentIdentityStrip';

/**
 * The frame every vault document screen sits in — the document page, the editor and the
 * review page.
 *
 * The three were built separately and each hand-rolled the same header, page width, back link
 * and identity row. They drifted: one screen's back link said "Back to Vault" and another's
 * "Back to document" for the same journey, the identity row called one fact three different
 * things, and one editor state rendered a bare header with no ``main`` and no way back at all.
 * Sharing the frame is what stops that recurring — a screen cannot forget a part it does not
 * own.
 *
 * Screens keep their own bodies. Only the surround lives here.
 */
export function VaultDocumentPage({
  title,
  subtitle,
  back,
  aside,
  identity,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** 'vault' → the library. `{ ref }` → the document this screen is about. */
  back: 'vault' | { ref: string };
  /** Optional right-hand helper beside the back link ("How editing works"). */
  aside?: ReactNode;
  /** The key-facts row. Omitted while the document is still loading. */
  identity?: { fields: IdentityField[]; state: string } | null;
  children: ReactNode;
}) {
  return (
    <>
      <PageHero title={title} subtitle={subtitle} />
      <main className="mx-auto max-w-[1280px] px-4 pt-8 pb-20 sm:px-6 lg:px-10">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {back === 'vault' ? (
            <Link
              to="/vault"
              className="inline-flex items-center gap-1 text-[13px] text-muted hover:text-navy"
            >
              <ArrowLeft className="size-4" /> Back to Vault
            </Link>
          ) : (
            <Link
              to="/vault/document/$ref"
              params={{ ref: back.ref }}
              className="inline-flex items-center gap-1 text-[13px] text-muted hover:text-navy"
            >
              <ArrowLeft className="size-4" /> Back to document
            </Link>
          )}
          {aside}
        </div>
        {identity && (
          <DocumentIdentityStrip className="mb-6" state={identity.state} fields={identity.fields} />
        )}
        {children}
      </main>
    </>
  );
}

/**
 * The key facts, composed identically for all three screens.
 *
 * Version used to be labelled "Version" here, "Editing version" in the editor and "Version under
 * review" on the review screen — three names for one fact, on three screens about one document.
 * Which screen you are on is already said by the header and the back link, so the fact is just
 * named. A document with no version yet SAYS so rather than dropping the cell, because a row that
 * silently changes shape between documents is the thing that reads as broken.
 */
export function documentIdentityFields({
  documentNumber,
  versionLabel,
  country,
}: {
  documentNumber: string | null | undefined;
  versionLabel: string | null | undefined;
  country: string | null | undefined;
}): IdentityField[] {
  const fields: IdentityField[] = [documentNumberField(documentNumber)];
  fields.push(
    versionLabel
      ? { label: 'Version', value: versionLabel, mono: true }
      : { label: 'Version', value: 'Not versioned', mono: false, muted: true },
  );
  if (country) fields.push({ label: 'Market', value: country });
  return fields;
}
